import {ActivatedRouteSnapshot, Router, RouterStateSnapshot} from '@angular/router';

import {LocalStorageService} from '@services/local-storage.service';
import {AuthenticationService} from '@auth/authentication.service';
import {NotificationService} from '@notifications';
import {CLIENT} from '@auth/constants';
import {ClientManagementService} from '@services/client-management.service';
import {SidebarService} from '@services/sidebar.service';
import {SelectedClientModel} from '@models/selected-client.model';
import {IClient} from '@shared/interface';
import {decodeReturnUrl, isConnectAiUrl} from '../../pages/connect-ai/connect-ai.util';
import {afterSignInUrl, isLoginUrl} from '@auth/sign-in-url';

/**
 * responsible to test if passed state is allowed.
 * Testing mechanism will be encapsulated in implementation. It might be
 * decision based on current authentication, based on stated metadata or combination
 * of two
 */
export interface StateFilter {

  /**
   * test if state is allowed
   * @param route represents route to activate
   * @param state represents route's state information
   */
  filter(route: ActivatedRouteSnapshot,
         state: RouterStateSnapshot): boolean;
}

/**
 * default implementation of {@link StateFilter} which divides state
 * check in two parts. The first part is test if implementation is responsible
 * for decision. In other words if implementation of this filter have to decide if we
 * can activate state or not. The second part is actual activation decision logic.
 * This class is an abstract and should be base for all user defined decision strategies
 * related to state activation
 */
export abstract class BaseSecurityFilter implements StateFilter {

  /**
   * Filter which is next in chain
   */
  private nextFilter: BaseSecurityFilter;

  /**
   * Attach next filter to this filter
   * @param nextFilter
   */
  setNextFilter(nextFilter: BaseSecurityFilter) {
    this.nextFilter = nextFilter;
  }

  /**
   * Implementation of filter method which test if this
   * filter is responsible for state activation decision. If it's true
   * then actual decision is delegated to {@link #isAllowed} method. If
   * this filter can't make decision then it delegates to {@link #nextFilter}
   * @param route represents route to activate
   * @param state represents route's state information
   * @returns {boolean}
   */
  filter(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    if (this.isApplicableToState(route, state)) {

      return this.isAllowed(route, state);

    } else if (this.nextFilter) {
      return this.nextFilter.filter(route, state);

    } else {
      return false;
    }
  }

  /**
   * check if this filter is responsible for state activation decision
   * @param route represents route to activate
   * @param state represents route's state information
   */
  protected abstract isApplicableToState(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean;

  /**
   * perform actual state activation decision logic
   * @param route represents route to activate
   * @param state represents route's state information
   */
  protected abstract isAllowed(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean;
}

/**
 * responsible to make decision about "login" state activation
 */
export class LoginFilter extends BaseSecurityFilter {

  constructor(private localStorageService: LocalStorageService,
              private router: Router) {
    super();
  }

  isApplicableToState(route: ActivatedRouteSnapshot,
                      state: RouterStateSnapshot): boolean {
    const publicPages = [
      '/login',
      '/login-admin',
      '/sign-up-admin',
      '/sign-up',
      '/lender-sign-up',
      '/sign-up/plans',
      '/sign-up/order',
      '/registration/complete',
      '/forgot-username',
      '/sign-up/xero',
      '/sign-up/phone-verification',
      '/page',
      '/reset-password',
      '/thank-you',
    ];

    /**
     * Zoho returns a new firm to the sign-up completion page with "?hostedpage_id=..." appended (card #1388),
     * which the exact match above never equals, so the page was treated as private and sent the payer to
     * sign in. It is matched on its path alone; every other public page keeps its exact match.
     * The connect version of the login page (card #1310) carries "?ref=/connect-ai..." onto the password and
     * username recovery pages, so those two are matched on their path as well.
     */
    const publicPagesMatchedByPath = ['/registration/complete', '/reset-password', '/forgot-username'];
    const isPublicPage = publicPages.indexOf(state.url) > -1 ||
      publicPagesMatchedByPath.indexOf(state.url.split('?')[0]) > -1;

    if (isLoginUrl(state.url)) {
      /**
       * A browser that is still signed in skips the form and goes where signing in from this address would send it
       * (card #1413). An expired session is cleared by the first call that gets 401, which reloads this page signed
       * out, so this cannot loop. Invitation links still sign the client in over any current session. Returning true
       * keeps the later filters (the client-profile redirect) from also navigating.
       */
      if (this.localStorageService.isAuthenticated() && !this.isInvitation(route)) {
        this.router.navigateByUrl(afterSignInUrl(decodeReturnUrl(route.queryParamMap.get('ref'))));
      }
      return true;
    }

    /**
     * case when user is authenticated and trying to get access to public pages
     */
    if (isPublicPage && this.localStorageService.isAuthenticated()) {
      this.router.navigate(['']);
      return false;
    }
    return isPublicPage;
  }

  isAllowed(route: ActivatedRouteSnapshot,
            state: RouterStateSnapshot): boolean {

    return this.localStorageService.isAnonymous() || (isLoginUrl(state.url) && this.isInvitation(route));
  }

  /** A login address with `?token=`, which signs a client in with that token. */
  private isInvitation(route: ActivatedRouteSnapshot): boolean {
    return route.queryParamMap.has('token');
  }
}

/**
 * responsible to make decision about public page activation
 */
export class AnonymousFilter extends BaseSecurityFilter {

  constructor(private localStorageService: LocalStorageService) {
    super();
  }

  isApplicableToState(route: ActivatedRouteSnapshot,
                      state: RouterStateSnapshot): boolean {

    return route.data && route.data['security'] && route.data['security']['publicPage'];
  }

  isAllowed(route: ActivatedRouteSnapshot,
            state: RouterStateSnapshot): boolean {
    // route "/password/setup" should be accessible for authenticated and anonymous users
    let passwordSetupUrl: string = '/password/setup?token=';

    return (state.url.includes(passwordSetupUrl)) ? state.url.includes(passwordSetupUrl) : this.localStorageService.isAnonymous();
  }
}

/**
 * Responsible to make decision about state activation which requires user to be authenticated
 */
export class AuthenticatedFilter extends BaseSecurityFilter {

  constructor(private authenticationService: AuthenticationService,
              private localStorageService: LocalStorageService,
              private notificationService: NotificationService,
              private router: Router,
              private clientManagementService: ClientManagementService,
              private sidebarService: SidebarService) {
    super();
  }

  isApplicableToState(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    /**
     * case when user is not authenticated and trying to get access to authenticated pages
     */
    if (this.localStorageService.isAnonymous()) {
      // Redirect to login with ref parameter containing the requested URL
      const returnUrl = encodeURIComponent(state.url);
      this.router.navigate(['/login'], { queryParams: { ref: returnUrl } });
      return false;
    }

    const pagesDependsOfSelectedClient = [
      '/banks/connections',
      '/banks/transactions',
      '/banks/transactions-verify',
      '/banks/running-balance',
      '/banks/statements',
      '/banks/checks',
      '/sq-management',
    ];
    const currentUser = this.localStorageService.getCurrentUserDetails();
    const currentUserIsClient = currentUser?.role === CLIENT;
    if (currentUserIsClient) {
      // A logged-in client is their own selected client; carry their id through.
      this.localStorageService.setSelectedClient({...currentUser, id: currentUser.userId});
      // The AI connect page (card #1310) always carries a query string, so it is matched on its path; it tells
      // a client "This sign-in is for accounting firms." instead of bouncing them to their profile.
      if (![
        '/client-profile',
        '/change-password',
        '/task-center/assigned-to-me',
        '/task-center/created-by-me',
        '/task-center/messages',
      ].includes(state.url) && !isConnectAiUrl(state.url)) {
        this.router.navigate(['/client-profile']);
        return true;
      }
    }

    const selectedClient = this.localStorageService.getSelectedClient();
    if (pagesDependsOfSelectedClient.indexOf(state.url) > -1 && !selectedClient) {
      // No client selected yet — instead of blocking the user with a
      // "Please select client" prompt, auto-select the first available client.
      // The client list is warmed up on app startup (see provideAppInitializer),
      // so it is normally available synchronously here.
      const snapshot = this.clientManagementService.clientsSnapshot;
      if (snapshot) {
        this.autoSelectFirstClient(snapshot);
        // fall through: navigation to the requested page continues below
      } else {
        // cache not warmed up yet (e.g. just logged in) — fetch, then forward
        this.clientManagementService.get().subscribe((clients) => {
          if (this.autoSelectFirstClient(clients)) {
            this.router.navigateByUrl(state.url);
          }
        });
        return false;
      }
    }

    return route.data && route.data['security'] && route.data['security']['authenticated'];
  }

  isAllowed(route: ActivatedRouteSnapshot,
            state: RouterStateSnapshot): boolean {
    return this.localStorageService.isAuthenticated();
  }

  /**
   * Auto-select the first available client so the user is not blocked on pages
   * that require a selected client. Returns true when a client was selected.
   * @param clients available clients
   */
  private autoSelectFirstClient(clients: IClient[]): boolean {
    const firstClient = clients?.[0];
    if (!firstClient) {
      // no clients exist at all — fall back to the previous behaviour
      this.router.navigate(['']);
      this.notificationService.error('Please select client');
      return false;
    }
    const autoSelected: SelectedClientModel = {
      id: firstClient.id,
      username: firstClient.username,
      fullName: firstClient.name || '',
      role: firstClient.role || '',
      accountsSize: (firstClient as { accountsSize?: number }).accountsSize || 0,
      phone: firstClient.phone || '',
      allowSms: firstClient.allowSms || false,
    };
    this.localStorageService.setSelectedClient(autoSelected);
    // keep the sidebar highlight in sync if it is already loaded
    this.sidebarService.pickClient(firstClient.username, true);
    return true;
  }
}

/**
 * responsible to make decision about state activation which requires role
 */
export class RoleBasedFilter extends BaseSecurityFilter {

  constructor(private authenticationService: AuthenticationService,
              private localStorageService: LocalStorageService,
              private notificationService: NotificationService,
              private router: Router) {
    super();
  }

  isApplicableToState(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    return route.data && route.data['security'] && route.data['security']['hasRole'];
  }

  isAllowed(route: ActivatedRouteSnapshot,
            state: RouterStateSnapshot): boolean {
    const isHasRoleDefined: string = route.data['security']['hasRole'];
    // a zone next to the role (SecurityCondition.hasRoleOrZone) lets its holders in too
    const zone: string[] = route.data['security']['hasZone'];
    return isHasRoleDefined && this.localStorageService.hasRole(route.data['security']['hasRole'])
      || !!zone && this.localStorageService.hasZone(zone);
  }
}

/**
 * responsible to make decision about state activation which requires zone
 */
export class ZoneBasedFilter extends BaseSecurityFilter {

  constructor(private localStorageService: LocalStorageService) {
    super();
  }

  isApplicableToState(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    return route.data && route.data['security'] && route.data['security']['hasZone'];
  }

  isAllowed(route: ActivatedRouteSnapshot,
            state: RouterStateSnapshot): boolean {
    const isHasZoneDefined: string = route.data['security']['hasZone'];
    return isHasZoneDefined && this.localStorageService.hasZone(route.data['security']['hasZone']);
  }
}

/**
 * default filter which simply reject any state activation
 */
export class RejectFilter extends BaseSecurityFilter {

  isApplicableToState(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    return true;
  }

  isAllowed(route: ActivatedRouteSnapshot,
            state: RouterStateSnapshot): boolean {

    return false;
  }
}
